/**
 * Geometry for the connection dialog's start ↔ rail transition (Option C, C1b, plan §3.2 "Measurement"). Pure.
 *
 * Every box is in DIALOG-LOCAL, UNSCALED coordinates: `(rect − dialogRect) / s` with `s = dialogRect.width /
 * dialog.offsetWidth` (the prototype's `frameScale`). That neutralises whatever moves or scales the whole dialog while a
 * flight is measured — Radix's entrance slide (`dialog.tsx`), the exit zoom, the UI scale — so a First box measured
 * before a commit and a Last box measured after it are comparable.
 *
 * Layers animate from their top-left corner (`transform-origin: 0 0`, `src/index.css`), so a translate is the
 * difference of the top-left corners and a uniform scale needs no centring term.
 */
export type Box = { x: number; y: number; w: number; h: number };

export type RectLike = { left: number; top: number; width: number; height: number };

/** The dialog's visual scale: its drawn width over its layout width (1 when either is unknown). */
export function frameScale(drawnWidth: number, layoutWidth: number): number {
  if (!(drawnWidth > 0) || !(layoutWidth > 0)) return 1;
  return drawnWidth / layoutWidth;
}

/** `rect` in the dialog's own unscaled coordinates. */
export function localBox(rect: RectLike, frame: RectLike, scale: number): Box {
  const s = scale > 0 ? scale : 1;
  return {
    x: (rect.left - frame.left) / s,
    y: (rect.top - frame.top) / s,
    w: rect.width / s,
    h: rect.height / s,
  };
}

export type Point = { x: number; y: number };

/** How far `from` lies from `to` (top-left corners): the translate that puts a layer laid out at `to` onto `from`. */
export function offsetBetween(from: Box, to: Box): Point {
  return { x: from.x - to.x, y: from.y - to.y };
}

/** A uniform scale `from / to`, 1 when either size is unknown (a layer that is not laid out, jsdom). */
export function uniformScale(from: number, to: number): number {
  if (!(from > 0) || !(to > 0)) return 1;
  return from / to;
}

const round = (value: number) => Math.round(value * 100) / 100;

export function translate({ x, y }: Point): string {
  return `translate(${round(x)}px, ${round(y)}px)`;
}

/** Translate then scale, both always written, so the two keyframes of a pair have the same function list. */
export function translateScale(point: Point, scale: number): string {
  return `${translate(point)} scale(${Math.round(scale * 1000) / 1000})`;
}

export const ORIGIN: Point = { x: 0, y: 0 };

export const px = (value: number) => `${round(value)}px`;
